import { StairTravelView } from "./models"
import { directionFromRaw } from "./util"

export const STAIR_TRANSITION_MS = 450
export const STAIR_WALK_SPEED = 80
export const STAIR_CLIMB_PX = 32
export const STAIR_UP_TRANSITION_MS = STAIR_CLIMB_PX / STAIR_WALK_SPEED * 1000

/** Walking stays in floor coordinates; the sink/rise is a separate body-only effect. */
export const stairTravelPose = (view: StairTravelView, elapsedMs: number) => {
    const elapsed = Math.max(0, Math.min(view.durationMs, elapsedMs))
    const departing = view.phase === "depart"
    const walkElapsed = departing ? elapsed : elapsed - view.transitionMs
    const walking = view.walkMs > 0 && (departing ? elapsed < view.walkMs : elapsed >= view.transitionMs && elapsed < view.durationMs)
    const walkProgress = view.walkMs ? Math.max(0, Math.min(1, walkElapsed / view.walkMs)) : 1
    const transition = Math.max(0, Math.min(1, (departing ? elapsed - view.walkMs : elapsed) / view.transitionMs))
    const smooth = transition * transition * (3 - 2 * transition)
    const depth = departing ? smooth : 1 - smooth
    const below = departing ? view.direction === "down" : view.direction === "up"
    const transitioning = departing ? elapsed >= view.walkMs : elapsed < view.transitionMs
    const ramp = view.ramp
    const upTile = departing ? view.direction === "up" : view.direction === "down"
    // Upright stairs keep floor coordinates at the base while the body climbs.
    // Fade while climbing in either direction so the room handoff has no stationary hold.
    if (ramp && upTile) {
        const transitionElapsed = Math.max(0, Math.min(view.transitionMs, departing ? elapsed - view.walkMs : elapsed))
        const progress = Math.min(1, transitionElapsed / Math.max(1, view.transitionMs))
        const climb = departing ? progress : 1 - progress
        const opacity = transitioning ? departing ? 1 - progress : progress : 1
        const start = departing ? view.start : ramp.entry
        const end = departing ? ramp.entry : view.end
        const climbing = transitioning && elapsed < view.durationMs
        return { location: transitioning ? { ...ramp.entry }
            : { x: start.x + (end.x - start.x) * walkProgress, y: start.y + (end.y - start.y) * walkProgress },
            walking: walking || climbing, transitioning, bodyOffset: -STAIR_CLIMB_PX * climb,
            opacity, below, entranceOffset: 0, headClip: undefined,
            direction: transitioning ? departing ? "Up" as const : "Down" as const : view.exitDirection,
            angle: transitioning ? departing ? -Math.PI / 2 : Math.PI / 2 : view.exitAngle }
    }
    // A floor opening uses the same fixed-ground rise on arrival and sink on
    // departure. Finish walking to its center before moving the body vertically.
    if (ramp && !upTile) {
        const center = departing ? view.end : view.start
        const start = departing ? view.start : center
        const end = departing ? center : view.end
        const entranceOffset = 12 * (transitioning ? 1 : departing ? walkProgress : 1 - walkProgress)
        return { location: transitioning ? { ...center }
            : { x: start.x + (end.x - start.x) * walkProgress, y: start.y + (end.y - start.y) * walkProgress },
            walking, transitioning, bodyOffset: 64 * depth - entranceOffset, below: true, entranceOffset,
            headClip: undefined, direction: view.exitDirection, angle: view.exitAngle }
    }
    const approachEnd = ramp ? (upTile ? ramp.entry : ramp.top) : view.end
    const exitStart = ramp ? (upTile ? ramp.entry : ramp.top) : view.start
    const start = !departing ? exitStart : view.start
    const end = departing ? approachEnd : view.end
    const departureHeight = view.direction === "down" ? 8 : 16
    const entranceOffset = departing && (ramp || view.direction === "down") ? departureHeight * walkProgress
        : ramp ? 16 * (transitioning ? 1 : 1 - walkProgress) : 0
    const from = ramp ? (departing === upTile ? ramp.entry : ramp.top) : view.start
    const to = ramp ? (departing === upTile ? ramp.top : ramp.entry) : view.end
    const location = ramp && transitioning
        ? { x: from.x + (to.x - from.x) * transition,
            y: from.y + (to.y - from.y) * transition }
        : { x: start.x + (end.x - start.x) * walkProgress,
            y: start.y + (end.y - start.y) * walkProgress }
    return { location, walking: walking || !!ramp && transitioning && elapsed < view.durationMs,
        // Downstairs departure starts 8px south of the usual 16px raised entrance.
        transitioning, bodyOffset: (ramp && upTile ? -24 * depth : (below ? 1 : -1) * 64 * depth) - entranceOffset, below, entranceOffset,
        headClip: ramp && upTile && transitioning ? depth : undefined,
        direction: ramp && transitioning ? directionFromRaw(from.x, from.y, to.x, to.y) ?? view.exitDirection : view.exitDirection,
        angle: ramp && transitioning ? Math.atan2(to.y - from.y, to.x - from.x) : view.exitAngle }
}
